import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GoogleLoginButton from '../components/GoogleLoginButton';
import { signInWithGoogle } from '../utils/googleAuth';

type LocationState = {
  email?: string;
  name?: string;
  picture?: string;
  fromGoogle?: boolean;
};

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, registerCompany, registerWithGoogle, registerCompanyWithGoogle, isAuthenticated } = useAuth();

  // LoginPage에서 Google 로그인 → 신규 사용자로 판정되면 state로 전달됨
  const locationState = (location.state || {}) as LocationState;
  const fromGoogle = Boolean(locationState.fromGoogle);

  const [mode, setMode] = useState<'team' | 'company'>('team'); // 팀원 가입 or 회사 생성
  const [email, setEmail] = useState(locationState.email || '');
  const [password, setPassword] = useState('');
  const [name, setName] = useState(locationState.name || '');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState(''); // 초대 코드
  const [companyName, setCompanyName] = useState(''); // 회사명 (회사 생성 시)
  const [isLoading, setIsLoading] = useState(false);

  // 이미 로그인된 경우 홈으로
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Google 가입: Firebase에서 받은 idToken으로 백엔드 호출
    if (fromGoogle) {
      // 팀원 가입 모드에서 초대 코드 필수 검증
      if (mode === 'team' && !inviteCode) {
        alert('초대 코드를 입력해주세요.');
        return;
      }
      // 회사 생성 모드에서 회사명 필수 검증
      if (mode === 'company' && !companyName) {
        alert('회사명을 입력해주세요.');
        return;
      }

      setIsLoading(true);
      try {
        const user = await signInWithGoogle();
        const idToken = await user.getIdToken();

        if (mode === 'company') {
          await registerCompanyWithGoogle(idToken, companyName);
        } else {
          await registerWithGoogle(idToken, inviteCode);
        }
        navigate('/');
      } catch (error: any) {
        console.log('[RegisterPage] Google 가입 에러:', error?.code, error?.message);
        // 에러는 AuthContext에서 toast로 표시
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // 이메일 가입 유효성 검사
    if (!email || !password || !name || !confirmPassword) {
      return;
    }

    if (password !== confirmPassword) {
      alert('비밀번호가 일치하지 않습니다.');
      return;
    }

    if (password.length < 6) {
      alert('비밀번호는 최소 6자 이상이어야 합니다.');
      return;
    }

    if (mode === 'team' && !inviteCode) {
      alert('초대 코드를 입력해주세요.');
      return;
    }

    if (mode === 'company' && !companyName) {
      alert('회사명을 입력해주세요.');
      return;
    }

    setIsLoading(true);
    try {
      if (mode === 'team') {
        await register({ email, password, name, inviteCode });
        navigate('/');
      } else {
        await registerCompany({ email, password, name, companyName });
        navigate('/');
      }
    } catch (error) {
      // 에러는 AuthContext에서 toast로 표시
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-pink-700 flex flex-col">
      {/* 회원가입 폼 영역 */}
      <div className="flex-grow flex items-center justify-center px-4 py-4">
        <div className="max-w-md w-full">
          {/* 로고 - 축소 */}
          <div className="text-center mb-4">
            <h1 className="text-4xl font-black mb-1">🧡</h1>
            <h2 className="text-2xl font-bold text-white mb-1">AZOCSII</h2>
            <p className="text-sm text-white/80">돈까스 아조씨</p>
          </div>

          {/* 회원가입 폼 - 압축 */}
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 shadow-2xl border border-white/20">
            <h3 className="text-xl font-bold text-white mb-3 text-center">회원가입</h3>

            {/* 모드 선택 탭 */}
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setMode('team')}
                className={`flex-1 py-2 px-3 rounded-xl text-sm font-semibold transition-all ${
                  mode === 'team'
                    ? 'bg-white/30 text-white'
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                팀원 가입
              </button>
              <button
                type="button"
                onClick={() => setMode('company')}
                className={`flex-1 py-2 px-3 rounded-xl text-sm font-semibold transition-all ${
                  mode === 'company'
                    ? 'bg-white/30 text-white'
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                회사 생성
              </button>
            </div>

            {/* ========== Google 가입 (상단) ========== */}

            {/* Google에서 넘어온 경우 배너 */}
            {fromGoogle && (
              <div className="mb-3 flex items-center gap-3 rounded-xl bg-white/15 px-3 py-2 border border-white/25">
                {locationState.picture && (
                  <img
                    src={locationState.picture}
                    alt=""
                    className="h-9 w-9 rounded-full border border-white/30"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white/80">Google 계정으로 가입 중</p>
                  <p className="text-sm text-white font-semibold truncate">{email}</p>
                </div>
              </div>
            )}

            {/* Google 가입 — fromGoogle이 아닌 경우에도 항상 표시 */}
            {!fromGoogle ? (
              <GoogleLoginButton
                className="w-full"
                label={mode === 'company' ? 'Google로 회사 생성하기' : 'Google로 팀원 가입하기'}
                onSuccess={async (firebaseUser) => {
                  navigate('/register', {
                    state: {
                      email: firebaseUser.email || '',
                      name: firebaseUser.displayName || '',
                      picture: firebaseUser.photoURL || undefined,
                      fromGoogle: true,
                    },
                  });
                }}
              />
            ) : (
              /* fromGoogle 상태 — 초대코드/회사명 입력 후 제출 */
              <form onSubmit={handleSubmit} className="space-y-3">
                {mode === 'team' ? (
                  <div>
                    <label htmlFor="inviteCode" className="block text-xs font-medium text-white mb-1">
                      초대 코드
                    </label>
                    <input
                      id="inviteCode"
                      type="text"
                      value={inviteCode}
                      onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                      required
                      className="w-full px-3 py-2.5 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent backdrop-blur-sm text-sm font-mono"
                      placeholder="ABC123-TEAM01"
                      disabled={isLoading}
                    />
                    <p className="mt-0.5 text-xs text-white/60">관리자에게 받은 초대 코드를 입력하세요</p>
                  </div>
                ) : (
                  <div>
                    <label htmlFor="companyName" className="block text-xs font-medium text-white mb-1">
                      회사명
                    </label>
                    <input
                      id="companyName"
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      required
                      className="w-full px-3 py-2.5 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent backdrop-blur-sm text-sm"
                      placeholder="우리 콜센터"
                      disabled={isLoading}
                    />
                    <p className="mt-0.5 text-xs text-white/60">회사 생성 후 팀원 초대 코드가 발급됩니다</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading || (mode === 'team' && !inviteCode) || (mode === 'company' && !companyName)}
                  className="w-full bg-gradient-to-r from-orange-500 to-pink-500 text-white font-bold py-2.5 px-6 rounded-xl hover:from-orange-600 hover:to-pink-600 transform hover:scale-105 transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isLoading ? '처리 중...' : (mode === 'team' ? 'Google로 팀원 가입' : 'Google로 회사 생성')}
                </button>
              </form>
            )}

            {/* ========== 구분선 ========== */}
            {!fromGoogle && (
              <>
                <div className="my-5 flex items-center gap-3">
                  <div className="h-px flex-1 bg-white/20" />
                  <span className="text-xs text-white/50">또는 이메일로 가입</span>
                  <div className="h-px flex-1 bg-white/20" />
                </div>

                {/* ========== 이메일 가입 (하단) ========== */}
                <form onSubmit={handleSubmit} className="space-y-3">
                  {/* 이름 */}
                  <div>
                    <label htmlFor="name" className="block text-xs font-medium text-white mb-1">
                      이름
                    </label>
                    <input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="w-full px-3 py-2.5 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent backdrop-blur-sm text-sm"
                      placeholder="홍길동"
                      disabled={isLoading}
                    />
                  </div>

                  {/* 이메일 */}
                  <div>
                    <label htmlFor="email" className="block text-xs font-medium text-white mb-1">
                      이메일
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-3 py-2.5 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent backdrop-blur-sm text-sm"
                      placeholder="your@email.com"
                      disabled={isLoading}
                    />
                  </div>

                  {/* 초대 코드 or 회사명 */}
                  {mode === 'team' ? (
                    <div>
                      <label htmlFor="inviteCode2" className="block text-xs font-medium text-white mb-1">
                        초대 코드
                      </label>
                      <input
                        id="inviteCode2"
                        type="text"
                        value={inviteCode}
                        onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                        required
                        className="w-full px-3 py-2.5 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent backdrop-blur-sm text-sm font-mono"
                        placeholder="ABC123-TEAM01"
                        disabled={isLoading}
                      />
                    </div>
                  ) : (
                    <div>
                      <label htmlFor="companyName2" className="block text-xs font-medium text-white mb-1">
                        회사명
                      </label>
                      <input
                        id="companyName2"
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        required
                        className="w-full px-3 py-2.5 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent backdrop-blur-sm text-sm"
                        placeholder="우리 콜센터"
                        disabled={isLoading}
                      />
                    </div>
                  )}

                  {/* 비밀번호 */}
                  <div>
                    <label htmlFor="password" className="block text-xs font-medium text-white mb-1">
                      비밀번호
                    </label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="w-full px-3 py-2.5 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent backdrop-blur-sm text-sm"
                      placeholder="••••••••"
                      disabled={isLoading}
                    />
                    <p className="mt-0.5 text-xs text-white/60">최소 6자 이상</p>
                  </div>

                  {/* 비밀번호 확인 */}
                  <div>
                    <label htmlFor="confirmPassword" className="block text-xs font-medium text-white mb-1">
                      비밀번호 확인
                    </label>
                    <input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="w-full px-3 py-2.5 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent backdrop-blur-sm text-sm"
                      placeholder="••••••••"
                      disabled={isLoading}
                    />
                  </div>

                  {/* 이메일 가입 버튼 */}
                  <button
                    type="submit"
                    disabled={isLoading || !email || !name || !password || !confirmPassword}
                    className="w-full bg-gradient-to-r from-orange-500 to-pink-500 text-white font-bold py-2.5 px-6 rounded-xl hover:from-orange-600 hover:to-pink-600 transform hover:scale-105 transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isLoading ? '가입 중...' : (mode === 'team' ? '이메일로 팀원 가입' : '이메일로 회사 생성')}
                  </button>
                </form>
              </>
            )}

            {/* 로그인 링크 */}
            <div className="mt-4 text-center">
              <p className="text-white/80 text-xs">
                이미 계정이 있으신가요?{' '}
                <Link
                  to="/login"
                  className="text-orange-300 hover:text-orange-200 font-semibold underline"
                >
                  로그인
                </Link>
              </p>
            </div>
          </div>

          {/* 홈으로 */}
          <div className="mt-3 text-center">
            <Link
              to="/"
              className="text-white/60 hover:text-white/90 text-xs transition-colors"
            >
              ← 홈으로 돌아가기
            </Link>
          </div>
        </div>
      </div>

      {/* Footer - 페이지 하단 */}
      <footer className="bg-primary text-white py-4">
        <div className="container mx-auto px-4 text-center">
          <p className="text-base mb-1">
            🧡{' '}
            <span className="text-red-500">A</span>
            <span className="text-orange-500">Z</span>
            <span className="text-yellow-400">O</span>
            <span className="text-green-500">C</span>
            <span className="text-blue-500">S</span>
            <span className="text-indigo-500">I</span>
            <span className="text-purple-500">I</span>
            {' '}- 돈까스 아조씨
          </p>
          <p className="text-xs text-gray-300">
            © 2026 AZOCSII Project.
          </p>
          <p className="text-xs text-secondary mt-0.5">
            <span className="font-bold text-accent">A</span>uthentic{' '}
            <span className="font-bold text-accent">Z</span>eal{' '}
            <span className="font-bold text-accent">O</span>asis —{' '}
            <span className="font-bold text-accent">C</span>ounselor{' '}
            <span className="font-bold text-accent">S</span>olidarity{' '}
            <span className="font-bold text-accent">I</span>nitiative
          </p>
          <p className="text-xs text-gray-400 mt-1">
            소명: 일상 회복 | A to Z, For Every Humanist in the World
          </p>
        </div>
      </footer>
    </main>
  );
};

export default RegisterPage;
